/** @format */

import { AnalyticsService, BreakpointsService } from '@shared/data-access/services';
import { CommonModule, isPlatformBrowser, Location } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	DestroyRef,
	OnInit,
	PLATFORM_ID,
	computed,
	inject,
	input,
	output,
	signal
} from '@angular/core';
import {
	HomefieldIconLibraryModule,
	HomefieldIconsLibraryService,
	homefieldIconRecommendationFire
} from '@dsg-tech/homefield-angular-icons';
import {
	ProductSocialContentState,
	SocialContentService,
	openSocialContentModal,
	setProductSocialContentState
} from '@social-content/data-access';

import { BreakPointNew } from '@shared/data-access/enums';
import { Environment } from '@shared/data-access/interfaces';
import { ReviewSocialImagesModalComponent } from '../review-social-images-modal/review-social-images-modal.component';
import { Store } from '@ngrx/store';
import { selectProductReviewCount } from '@product-reviews/data-access';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
	selector: 'pdp-hot-on-social-button',
	standalone: true,
	imports: [CommonModule, HomefieldIconLibraryModule, ReviewSocialImagesModalComponent],
	styleUrl: './hot-on-social-button.component.scss',
	templateUrl: './hot-on-social-button.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class HotOnSocialButtonComponent implements OnInit {
	private readonly location = inject(Location);
	seoUrl = input.required<string>();
	buttonClick = output<void>();
	socialContentService = inject(SocialContentService);
	environment = inject(Environment);
	platFormId = inject(PLATFORM_ID);
	breakPointsService = inject(BreakpointsService);
	private readonly analyticsService = inject(AnalyticsService);
	private readonly destroyRef = inject(DestroyRef);
	store: Store<ProductSocialContentState> = inject(Store);

	isMobileSize = computed(() => this.breakPointsService.breakpoint() <= BreakPointNew.MEDIUM);
	shouldShowButton = signal(false);
	hasReviews = signal(false);
	private readonly reviewCount$ = this.store.select(selectProductReviewCount);

	constructor(homefieldIconsLibraryService: HomefieldIconsLibraryService) {
		homefieldIconsLibraryService.registerIcons([homefieldIconRecommendationFire]);
	}

	ngOnInit(): void {
		if (isPlatformBrowser(this.platFormId)) {
			const seoUrlValue = this.seoUrl() || this.getFallBackSeoUrl();
			const productUrl = `${this.environment.thirdParty.bv.socialApi.brandDomains.dsg}${seoUrlValue}`;
			this.socialContentService
				.getSocialContentByProductUrl$(productUrl)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe((response) => {
					this.shouldShowButton.set(!!response.resultsCount);
					this.store.dispatch(
						setProductSocialContentState({
							items: response.items,
							resultsCount: response.resultsCount
						})
					);
				});
		}

		this.reviewCount$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((reviewCount) => {
			this.hasReviews.set(!!reviewCount);
		});
	}

	private getFallBackSeoUrl(): string {
		const path = this.location.path();
		// Extract everything starting with /p/ up to (but not including) any query string
		const match = path.match(/(\/p\/[^?]*)/);
		return match ? match[1] : '';
	}

	onClick(): void {
		this.buttonClick.emit();
		this.store.dispatch(openSocialContentModal());
		this.analyticsService.triggerAnalyticsEvent('multiUseAnalyticsEvent', {
			event: 'pdp_HotOnSocial'
		});
	}
}
